import axios, { axiosInstanceForRefreshToken } from 'api';
import { disablePush } from 'lib/push';

// Super-admin password login. The general /api/account/login/ endpoint
// rejects admin/tenant users (forces them through the OTP funnel); this
// dedicated endpoint accepts email+password for is_superuser=True accounts
// and the backend gates it with: request.tenant.schema_name == "public".
//
// Two things have to be true on the wire for that gate to pass:
//
//   1. No stale `orphix_sessionid` cookie with `active_schema=<tenant>`
//      in the browser. The tenant middleware honours that cookie above
//      headers and hostname, so a leftover session from a previous
//      tenant login would drag the request into the wrong schema and
//      we'd get a 403 "Super-admin login must hit the public-schema
//      host." We call /api/account/logout/ first to nuke it server-side
//      (the cookie is HttpOnly — JS can't delete it directly).
//
//   2. `X-Tenant-Schema: public` on the request. Hostname resolution
//      depends on the Domain row mapped to whatever host the admin
//      panel is served from — sending the header forces public
//      regardless of how that mapping is configured.
export const login = async (payload) => {
  // Best-effort logout: 200 either way, swallows errors (the user may
  // not have had a session at all). Done synchronously before the POST
  // so the cookie is cleared before super-admin login fires.
  try {
    await axios.post('/api/account/logout/', {}, { withCredentials: true });
  } catch (_) {
    /* idempotent; ignore network blips */
  }

  return axios.post(
    `/api/account/super-admin/login/`,
    {
      email: payload.email,
      password: payload.password
    },
    {
      withCredentials: true,
      headers: { 'X-Tenant-Schema': 'public' }
    }
  );
};

export const refreshTokenAPI = (params) => {
  const user = {
    refresh: params
  };
  return axiosInstanceForRefreshToken.post(`/api/account/refresh_token/`, user);
};

export const reCaptchaVarify = (params) => {
  return axios.post('api/account/captcha_verify/', params);
};

/**
 * Sign-out teardown for push.
 *
 * Deactivates this browser's platform device row BEFORE the session is torn
 * down — the endpoint is session-authenticated, so afterwards the row can
 * never be cleaned up and the backend keeps pushing platform alerts, which
 * name tenants and failures, to a browser nobody is signed into.
 *
 * Never throws. Call this from wherever logout is dispatched, ahead of the
 * store reset; it is time-boxed internally so it cannot hang the sign-out.
 */
export const teardownPushOnLogout = () => disablePush();
